import { useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TrendingDown, Calendar } from "lucide-react";
import { api } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function TimeseriesChart({ refreshKey }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api
      .timeseries(days)
      .then(setData)
      .catch(() => setData(null));
  }, [days, refreshKey]);

  if (!data) {
    return (
      <div className="cre-surface rounded-sm p-6 text-sm text-zinc-500">
        Loading savings trend…
      </div>
    );
  }

  const totalMin = data.cumulative_minutes_saved || 0;
  const hours = (totalMin / 60).toFixed(1);

  const points = (data.points || []).map((p) => ({
    ...p,
    label: fmtDate(p.date),
  }));

  return (
    <div className="cre-surface rounded-sm p-6" data-testid="timeseries-chart">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm border border-emerald-500/40 bg-emerald-500/10 flex items-center justify-center">
            <TrendingDown className="h-4 w-4 text-emerald-300" strokeWidth={1.5} />
          </div>
          <div>
            <div className="cre-overline text-emerald-300">Cumulative impact</div>
            <h3 className="font-heading text-lg font-bold tracking-tight">
              MTTR savings — last {days} days
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-1 border border-white/10 rounded-sm p-1 font-mono text-[11px]">
          {[7, 14, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`range-${d}d`}
              className={`px-2 py-1 rounded-sm transition-colors ${
                days === d
                  ? "bg-sky-100 text-sky-700"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="border border-white/10 rounded-sm p-3">
          <div className="cre-overline">Total minutes saved</div>
          <div className="mt-0.5 font-heading font-black text-2xl text-emerald-300">
            {Math.round(totalMin)}
          </div>
        </div>
        <div className="border border-white/10 rounded-sm p-3">
          <div className="cre-overline">Equivalent hours</div>
          <div className="mt-0.5 font-heading font-black text-2xl text-blue-300">
            {hours}
          </div>
        </div>
        <div className="border border-white/10 rounded-sm p-3 col-span-2 md:col-span-1">
          <div className="cre-overline">Approved incidents</div>
          <div className="mt-0.5 font-heading font-black text-2xl text-zinc-100">
            {data.cumulative_incidents_approved || 0}
          </div>
        </div>
      </div>

      <div className="mt-5 h-56">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 4, right: 12, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="cre-savings" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22c55e" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#22c55e" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(15,23,42,0.08)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: "#64748b", fontSize: 10, fontFamily: "JetBrains Mono" }}
              axisLine={{ stroke: "rgba(15,23,42,0.12)" }}
              tickLine={false}
              minTickGap={20}
            />
            <YAxis
              tick={{ fill: "#64748b", fontSize: 10, fontFamily: "JetBrains Mono" }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip
              contentStyle={{
                background: "#ffffff",
                border: "1px solid rgb(226 232 240)",
                borderRadius: 4,
                fontSize: 12,
                fontFamily: "JetBrains Mono",
                color: "#0f172a",
              }}
              labelStyle={{ color: "#475569" }}
              formatter={(value, name) => {
                if (name === "minutes_saved") return [`${value} min`, "Saved"];
                if (name === "incidents_approved") return [value, "Approved"];
                return [value, name];
              }}
            />
            <Area
              type="monotone"
              dataKey="minutes_saved"
              stroke="#22c55e"
              strokeWidth={2}
              fill="url(#cre-savings)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2 flex items-center gap-2 text-[11px] font-mono text-zinc-500">
        <Calendar className="h-3 w-3" />
        Computed daily from approved incidents using current org baselines.
      </div>
    </div>
  );
}
